import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star, Award, Users, Clock, Shield, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c42ba4e9-9f23-42d1-8dca-60d6e029ce6f/artifacts/b4bmdmcp_IMG_7020.jpeg";
const TEAM_IMG = "https://images.pexels.com/photos/15798782/pexels-photo-15798782.jpeg?auto=compress&cs=tinysrgb&w=800";

const stats = [
  { value: "500+", label: "Projects Completed", icon: Award },
  { value: "15+", label: "Years Experience", icon: Clock },
  { value: "100%", label: "Licensed & Insured", icon: Shield },
  { value: "24/7", label: "Emergency Service", icon: Phone },
];

const values = [
  {
    title: "Quality Craftsmanship",
    desc: "Every project is completed with meticulous attention to detail and premium materials.",
  },
  {
    title: "Honest Communication",
    desc: "We believe in transparent pricing and keeping you informed at every step.",
  },
  {
    title: "Customer First",
    desc: "Your satisfaction is our top priority. We don't stop until you're happy.",
  },
  {
    title: "Community Focused",
    desc: "As a local Memphis company, we take pride in building up our community.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      {/* Header */}
      <section className="bg-brand-dark py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
              Our Story
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white">
              About Us
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <img
                  src={TEAM_IMG}
                  alt="Our team at work"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute -bottom-6 -right-6 bg-brand p-6 hidden md:block">
                  <span className="font-outfit font-black text-4xl text-white block">
                    15+
                  </span>
                  <span className="font-outfit text-sm uppercase tracking-wider text-white/80">
                    Years
                  </span>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
                Who We Are
              </span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight uppercase text-brand-dark mb-6">
                Building Dreams
                <br />
                Since Day One
              </h2>
              <div className="space-y-4 font-dm-sans text-neutral-600 leading-relaxed">
                <p>
                  Mid America Star Builders LLC is a full-service general
                  contractor proudly serving Memphis, Tennessee and the
                  surrounding areas. Founded by Mario Jacob, our company was
                  built on the principles of quality workmanship, honest
                  communication, and customer satisfaction.
                </p>
                <p>
                  From roofing and painting to complete home renovations and fire
                  & water restoration, we bring expertise and dedication to every
                  project. Our team of skilled professionals treats every home
                  like their own, ensuring the highest standards of quality.
                </p>
                <p>
                  We understand that your home is your biggest investment.
                  That's why we offer financing options to make your dream
                  projects a reality. Contact us today for a free estimate.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 md:py-20 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="text-center"
                >
                  <Icon className="w-8 h-8 text-brand mx-auto mb-3" />
                  <span className="font-outfit font-black text-3xl sm:text-4xl text-white block">
                    {stat.value}
                  </span>
                  <span className="font-dm-sans text-sm text-neutral-400">
                    {stat.label}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
              What Drives Us
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight uppercase text-brand-dark">
              Our Values
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-neutral-200">
            {values.map((val, i) => (
              <motion.div
                key={val.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="p-8 md:p-12 border border-neutral-200"
              >
                <div className="w-10 h-10 bg-brand flex items-center justify-center mb-4">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-outfit text-xl font-bold tracking-tight text-brand-dark mb-3">
                  {val.title}
                </h3>
                <p className="font-dm-sans text-neutral-600 leading-relaxed">
                  {val.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Mario */}
      <section className="py-20 md:py-28 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <img
              src={LOGO_URL}
              alt="MASB Logo"
              className="w-24 h-24 mx-auto mb-8 object-contain"
            />
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
              Meet the Founder
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight uppercase text-brand-dark mb-6">
              Mario Jacob
            </h2>
            <p className="font-dm-sans text-neutral-600 leading-relaxed mb-8">
              As the founder and lead contractor of Mid America Star Builders,
              Mario Jacob brings passion, expertise, and a commitment to
              excellence on every project. His hands-on approach and dedication
              to quality have made MASB one of Memphis's trusted contractors.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:9014061803">
                <Button className="bg-brand text-white font-outfit font-bold uppercase tracking-wider text-sm px-8 py-5 hover:bg-brand-hover rounded-none">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Mario
                </Button>
              </a>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-2 border-brand-dark text-brand-dark font-outfit font-bold uppercase tracking-wider text-sm px-8 py-5 hover:bg-brand-dark hover:text-white rounded-none"
                >
                  Send a Message
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
